import { HttpClient } from './httpClient.ts'

export interface FreeTalkRole {
  id: string
  name: string
  description: string
  hints: string[]
}

export interface FreeTalkStartResponse {
  role: FreeTalkRole
  greeting: string
  greeting_translation: string
  audio_url: string | null
  level: string
  user_name: string
}

export interface FreeTalkHistoryItem {
  role: 'ai' | 'user'
  text: string
}

export interface FreeTalkTurnResponse {
  status: 'ok' | 'unrecognized'
  transcript: string
  reply: string
  reply_translation: string
  audio_url: string | null
  duration_ms: number
  language: string
}

export interface FreeTalkSummaryTurn extends FreeTalkHistoryItem {
  duration_ms?: number
}

export interface FreeTalkMistake {
  said: string
  better: string
  explanation_ru: string
  audio_url: string | null
}

export interface FreeTalkWord {
  word: string
  translation: string
}

export interface FreeTalkSummaryResponse {
  mistakes: FreeTalkMistake[]
  words: FreeTalkWord[]
  speech_seconds: number
  user_turns: number
}

export class FreeTalkApi {
  constructor(private httpClient: HttpClient) {}

  start(roleId: string) {
    return this.httpClient.post<FreeTalkStartResponse>(
      '/api/free_talk/start/',
      {
        role_id: roleId,
      },
    )
  }

  turn(
    audio: Blob,
    roleId: string,
    history: FreeTalkHistoryItem[],
    signal?: AbortSignal,
  ) {
    const form = new FormData()
    form.append(
      'audio',
      audio,
      'turn.' + (audio.type.includes('mp4') ? 'mp4' : 'webm'),
    )
    form.append('role_id', roleId)
    form.append('history', JSON.stringify(history))
    // Errors are surfaced inside the conversation (ER-04/ER-05), not as toasts.
    return this.httpClient.request<FreeTalkTurnResponse>(
      '/api/free_talk/turn/',
      { method: 'POST', body: form, signal },
      false,
      false,
    )
  }

  summary(
    turns: FreeTalkSummaryTurn[],
    knownWords: string[],
    signal?: AbortSignal,
  ) {
    return this.httpClient.request<FreeTalkSummaryResponse>(
      '/api/free_talk/summary/',
      {
        method: 'POST',
        body: JSON.stringify({ turns, known_words: knownWords }),
        headers: this.httpClient.getHeaders(),
        signal,
      },
      false,
      false,
    )
  }

  translate(text: string) {
    return this.httpClient.post<{ translation: string }>(
      '/api/free_talk/translate/',
      { text },
    )
  }
}
